import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  IconChart,
  IconDashboard,
  IconTrendDown,
  IconScale,
  IconHedge,
  IconTarget,
  IconBook,
  IconCalculator,
  IconHelp,
  IconFile,
  IconUpload,
  IconUser,
  IconUsers,
  IconStar,
  IconShield,
  IconList,
  IconChevronRight,
  IconZap,
  IconPlus,
  IconAlert,
  IconLock,
} from './Icons';
import ThemeToggle from './ThemeToggle';
import { hasClientReportingAccess, hasMyResultsAccess } from '../utils/tierAccess';
import './Sidebar.css';
import EntitySwitcher from './EntitySwitcher';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { getResultsNavLabel, ENTITY_CHANGED_EVENT, isVectorEntity } from '../utils/activeEntity';
import { useWorkspace } from '../utils/workspace';
import { useBrandIdentity } from '../utils/brandIdentity';

const SHOW_DESKTOP_APP_GUIDE_MENU = false;

function Sidebar({
  currentPage,
  riskMode,
  onNavigate,
  onHome = null,
  onModeChange,
  onUpgrade,
  isPro,
  isMax,
  isVectorAdmin,
  isBeta,
  isDevMode,
  isAdmin,
  isVectorAdminPlus,
  isSupport = false,
  isSales = false,
  canSeeTradeWatcher = false,
  // Users → Feature access switches; default shows everything that was live.
  canSeeFeature = () => true,
  isFeatureLocked = () => false,
  isOpen = false,
  onClose,
  collapsed = false,
  onToggleCollapsed,
  theme = 'dark',
  onToggleTheme,
}) {
  // The brand's own name, logo and tagline (RiskLo only for the RiskLo home brand).
  const brandIdentity = useBrandIdentity();
  // Older admin tools (Metrics History): Platform Admins and Vector's Brand
  // Admins. Strategy tools live on the Strategies page.
  const showLegacyTools = Boolean(isAdmin || isVectorEntity());
  const { user, tierReady, isSelfUsePreview } = useAuth();
  // Not part of a company (self use): plain "Results", no company name.
  const workspace = useWorkspace(Boolean(user?.email));
  // Self users have no company Results; Results is hidden for them for now.
  const hideResults = isSelfUsePreview || (workspace.selfUser && !workspace.isFullAdmin && !isAdmin);

  // "<Brand> Results" — follows the active entity (Vector Results / Kairos
  // Results). Re-reads on switch so the label updates without a reload.
  const [resultsNavLabel, setResultsNavLabel] = useState(() => getResultsNavLabel());
  useEffect(() => {
    const sync = () => setResultsNavLabel(getResultsNavLabel());
    window.addEventListener(ENTITY_CHANGED_EVENT, sync);
    return () => window.removeEventListener(ENTITY_CHANGED_EVENT, sync);
  }, []);

  const [pendingSubmissionCount, setPendingSubmissionCount] = useState(0);
  const [adminAlertCount, setAdminAlertCount] = useState(0);
  const [tradeWatchCount, setTradeWatchCount] = useState(0);

  useEffect(() => {
    if (!isAdmin) {
      setPendingSubmissionCount(0);
      return undefined;
    }
    let cancelled = false;
    const loadPendingCount = async () => {
      try {
        const res = await fetch(
          API_ENDPOINTS.adminStrategySubmissions({ status: 'pending', limit: 1 }),
          { headers: getBearerAuthHeaders() },
        );
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.success) {
          setPendingSubmissionCount(Number(data.total) || 0);
        }
      } catch (_e) { /* optional */ }
    };
    loadPendingCount();
    return () => { cancelled = true; };
  }, [isAdmin, currentPage]);

  useEffect(() => {
    if (!isAdmin) {
      setAdminAlertCount(0);
      return undefined;
    }
    let cancelled = false;
    const loadAlertCount = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.adminAlertsCount, { headers: getBearerAuthHeaders() });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.success) {
          setAdminAlertCount(Number(data.count) || 0);
        }
      } catch (_e) { /* optional */ }
    };
    loadAlertCount();
    const onAlertsChanged = () => loadAlertCount();
    window.addEventListener('admin-alerts-changed', onAlertsChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('admin-alerts-changed', onAlertsChanged);
    };
  }, [isAdmin, currentPage]);

  useEffect(() => {
    if (!isAdmin) {
      setTradeWatchCount(0);
      return undefined;
    }
    let cancelled = false;
    const loadTradeWatchCount = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.tradeWatchAlertsCount, { headers: getBearerAuthHeaders() });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.success) {
          setTradeWatchCount(Number(data.count) || 0);
        }
      } catch (_e) { /* optional */ }
    };
    loadTradeWatchCount();
    // An unprotected position is urgent, so refresh the badge while the tab is visible.
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') loadTradeWatchCount();
    }, 30000);
    window.addEventListener('trade-watch-changed', loadTradeWatchCount);
    return () => {
      cancelled = true;
      clearInterval(poll);
      window.removeEventListener('trade-watch-changed', loadTradeWatchCount);
    };
  }, [isAdmin, currentPage]);

  // Close the mobile drawer on Escape
  useEffect(() => {
    if (!isOpen || !onClose) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Wrap nav so picking an item also closes the drawer on mobile. Harmless
  // on desktop since onClose just toggles a flag the desktop layout ignores.
  const handleNavigate = useCallback((page) => {
    onNavigate(page);
    if (onClose) onClose();
  }, [onNavigate, onClose]);

  const handleModeChange = useCallback((mode) => {
    onModeChange(mode);
    if (onClose) onClose();
  }, [onModeChange, onClose]);

  const handleUpgrade = useCallback(() => {
    if (onUpgrade) onUpgrade();
    if (onClose) onClose();
  }, [onUpgrade, onClose]);

  // Dashboard is visible to every signed-in tier. Basic and Pro see a
  // read-only view of the org-wide Vector Dashboard; Max/Admin/VectorAdmin
  // additionally see their personal dashboards in the picker.
  const hasDashboardAccess = true;

  // Tier label priority: Platform Admin (badge "Admin") > Brand Admin Plus > Brand Admin > Support > Sales > Max > Beta > Pro > Basic.
  const tierLabel = !tierReady && user
    ? '···'
    : isAdmin
      ? 'Admin'
      : isVectorAdminPlus
        ? 'Brand Admin+'
        : isVectorAdmin
          ? 'Brand Admin'
          : isSupport
            ? 'Support'
          : isSales
            ? 'Sales'
          : isMax
            ? 'Max'
            : isBeta
              ? 'Beta'
              : isPro
                ? 'Pro'
                : 'Basic';

  const tierBadgeClass = !tierReady && user
    ? 'sidebar-badge-basic'
    : isAdmin
      ? 'sidebar-badge-admin'
      : isVectorAdminPlus
        ? 'sidebar-badge-vadmin-plus'
        : isVectorAdmin
          ? 'sidebar-badge-vadmin'
          : isSupport
            ? 'sidebar-badge-support'
          : isSales
            ? 'sidebar-badge-max'
          : isMax
            ? 'sidebar-badge-max'
            : isBeta
              ? 'sidebar-badge-beta'
              : isPro
                ? 'sidebar-badge-pro'
                : 'sidebar-badge-basic';

  const isHome = currentPage === 'home';
  const blowoutActive = isHome && riskMode === 'risk';
  const ruleActive = isHome && riskMode === 'apexMae';
  const resultsMainActive = currentPage === 'results';
  const portfolioActive = currentPage === 'portfolio-simulator';
  const hedgingRiskActive = currentPage === 'hedging-risk';
  const propPlannerActive = currentPage === 'prop-firm-planner';
  const myResultsActive = currentPage === 'my-results';
  const clientReportingActive = currentPage === 'client-reporting';
  const spreadsheetActive = currentPage === 'results-spreadsheet';
  const dashboardActive = currentPage === 'dashboard';
  const hasMyResultsAccessFlag = hasMyResultsAccess({ isAdmin });
  const hasClientReportingAccessFlag = hasClientReportingAccess({
    isSignedIn: Boolean(user),
    isPro,
    isMax,
    isBeta,
    isAdmin,
    isVectorAdmin,
  });

  // Admin → Brands → Features: locked items stay in the menu, grayed out with a
  // lock, and open a preview of the page.
  const lockedClass = (key) => (isFeatureLocked(key) ? ' is-locked' : '');
  const lockBadge = (key) => (isFeatureLocked(key) ? (
    <span className="sidebar-lock-badge" title="Not included in your brand's plan" aria-label="Locked">
      <IconLock size={13} />
    </span>
  ) : null);
  const proBadgeUnlessLocked = (key, badge) => (isFeatureLocked(key) ? lockBadge(key) : badge);

  const sidebarProBadge = !isPro ? (
    <span className="sidebar-pro-badge" aria-label="Pro feature">
      PRO
    </span>
  ) : null;

  const initials = (() => {
    const source = user?.name || user?.email || '';
    if (!source) return '';
    const parts = source.split(/[\s@.]+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  })();

  return (
    <>
      {/* Mobile-only backdrop. Pointer events live on the overlay so tapping
          outside the drawer closes it. CSS hides this completely on desktop. */}
      <div
        className={`risklo-sidebar-backdrop ${isOpen ? 'is-visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`risklo-sidebar ${isOpen ? 'is-open' : ''} ${collapsed ? 'is-collapsed' : ''}`}
        aria-label="Primary navigation"
      >
        {/* Desktop-only collapse toggle — shrinks the rail to icons-only. */}
        {onToggleCollapsed ? (
          <button
            type="button"
            className="sidebar-collapse-toggle"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <IconChevronRight size={16} />
          </button>
        ) : null}
        <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-brand"
          onClick={() => {
            // When collapsed, clicking the logo re-expands the rail instead of
            // navigating, so it doubles as the expand control.
            if (collapsed && onToggleCollapsed) {
              onToggleCollapsed();
              return;
            }
            if (onHome) {
              onHome();
              if (onClose) onClose();
            } else {
              handleNavigate('home');
            }
          }}
          aria-label={collapsed ? 'Expand sidebar' : `${brandIdentity.appName} home`}
        >
          <img
            src={brandIdentity.logoUrl}
            alt=""
            aria-hidden="true"
            className="sidebar-brand-logo"
          />
          <div className="sidebar-brand-text">
            {/* The name gets the whole row (wrapping onto a second line if it must,
                never cut off). The tier badge sits under it, beside the tagline. */}
            <span
              className={`sidebar-brand-title${String(brandIdentity.appName || '').length > 10 ? ' sidebar-brand-title--long' : ''}`}
            >
              {brandIdentity.appName}{brandIdentity.isPlatform ? <sup className="reg-mark">®</sup> : null}
            </span>
            <div className="sidebar-brand-meta-row">
              {brandIdentity.tagline ? <span className="sidebar-brand-subtitle">{brandIdentity.tagline}</span> : null}
              <span
                className={`sidebar-tier-badge sidebar-tier-badge--compact ${tierBadgeClass} ${isDevMode ? 'sidebar-badge-dev' : ''}`}
                aria-busy={Boolean(user && !tierReady)}
              >
                {tierLabel}
              </span>
            </div>
          </div>
        </button>
      </div>

      {/* Brand (entity) picker — Vector / Kairos / …. Renders nothing when the
          user has no entities, and a static label when they have exactly one. */}
      <EntitySwitcher collapsed={collapsed} />

      <nav className="sidebar-nav">
        <div className="sidebar-section">
          <div className="sidebar-section-title">Results</div>
          <ul className="sidebar-section-list">
            {tierReady && hasDashboardAccess && canSeeFeature('overview') && (
              <li>
                <button
                  type="button"
                  className={`sidebar-link ${dashboardActive ? 'active' : ''}${lockedClass('overview')}`}
                  onClick={() => handleNavigate('dashboard')}
                >
                  <span className="sidebar-link-icon"><IconDashboard size={18} /></span>
                  <span className="sidebar-link-label">Overview</span>
                  {lockBadge('overview')}
                </button>
              </li>
            )}
            {tierReady && user && canSeeFeature('my_portfolio') && (
              <li>
                <button
                  type="button"
                  className={`sidebar-link ${clientReportingActive ? 'active' : ''}${lockedClass('my_portfolio')}`}
                  onClick={() => handleNavigate('client-reporting')}
                >
                  <span className="sidebar-link-icon"><IconUsers size={18} /></span>
                  <span className="sidebar-link-label">My Portfolio</span>
                  {proBadgeUnlessLocked('my_portfolio', !hasClientReportingAccessFlag ? sidebarProBadge : null)}
                </button>
              </li>
            )}
            {!hideResults && canSeeFeature('brand_results') && (
            <li>
              <button
                type="button"
                className={`sidebar-link ${resultsMainActive ? 'active' : ''}${lockedClass('brand_results')}`}
                onClick={() => handleNavigate('results')}
              >
                <span className="sidebar-link-icon"><IconChart size={18} /></span>
                <span className="sidebar-link-label">{workspace.selfUser ? 'Results' : resultsNavLabel}</span>
                {lockBadge('brand_results')}
              </button>
            </li>
            )}
            {false && (
              <li>
                <button
                  type="button"
                  className={`sidebar-link ${spreadsheetActive ? 'active' : ''}`}
                  onClick={() => handleNavigate('results-spreadsheet')}
                >
                  <span className="sidebar-link-icon"><IconFile size={18} /></span>
                  <span>Results Spreadsheet</span>
                </button>
              </li>
            )}
            {canSeeFeature('portfolio_simulator') && (
            <li>
              <button
                type="button"
                className={`sidebar-link ${portfolioActive ? 'active' : ''}${lockedClass('portfolio_simulator')}`}
                onClick={() => handleNavigate('portfolio-simulator')}
              >
                <span className="sidebar-link-icon"><IconList size={18} /></span>
                <span className="sidebar-link-label">Portfolio Simulator</span>
                {proBadgeUnlessLocked('portfolio_simulator', sidebarProBadge)}
              </button>
            </li>
            )}
            {tierReady && hasMyResultsAccessFlag && canSeeFeature('my_results') && (
              <li>
                <button
                  type="button"
                  className={`sidebar-link ${myResultsActive ? 'active' : ''}${lockedClass('my_results')}`}
                  onClick={() => handleNavigate('my-results')}
                >
                  <span className="sidebar-link-icon"><IconChart size={18} /></span>
                  <span className="sidebar-link-label">My Results</span>
                  {lockBadge('my_results')}
                </button>
              </li>
            )}
          </ul>
        </div>

        {/* Risk Tools: Trade Watcher (every account, any broker) on top, then the
            portfolio tools and the prop firm tools as subgroups. Portfolio first:
            most people are trading brokerage accounts now. */}
        <div className="sidebar-section">
          <div className="sidebar-section-title">Risk Tools</div>
          <ul className="sidebar-section-list">
            {user && canSeeTradeWatcher && (
              <li>
                <button
                  type="button"
                  className={`sidebar-link ${currentPage === 'trade-watcher' ? 'active' : ''}${lockedClass('trade_watcher')}`}
                  onClick={() => handleNavigate('trade-watcher')}
                >
                  <span className="sidebar-link-icon"><IconShield size={18} /></span>
                  <span className="sidebar-link-label">Trade Watcher</span>
                  {/* Full Admin: unprotected positions, shown on Trade Watcher → Alerts. */}
                  {tradeWatchCount > 0 ? (
                    <span className="sidebar-pending-count" aria-label={`${tradeWatchCount} unprotected positions`}>
                      {tradeWatchCount}
                    </span>
                  ) : proBadgeUnlessLocked('trade_watcher', sidebarProBadge)}
                </button>
              </li>
            )}
          </ul>
          <div className="sidebar-section-title sidebar-subsection-title" title="For stocks, ETFs and crypto in your brokerage accounts">Portfolio</div>
          <ul className="sidebar-section-list">
            {[
              { page: 'position-sizer', label: 'Position Sizer', Icon: IconCalculator },
              { page: 'concentration-risk', label: 'Concentration Risk', Icon: IconAlert },
              { page: 'drawdown-risk', label: 'Drawdown & VaR', Icon: IconChart },
              { page: 'stop-planner', label: 'Stop Planner', Icon: IconShield },
              { page: 'rebalance', label: 'Rebalancing', Icon: IconScale },
            ].map(({ page, label, Icon }) => (
              <li key={page}>
                <button
                  type="button"
                  className={`sidebar-link ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handleNavigate(page)}
                >
                  <span className="sidebar-link-icon"><Icon size={18} /></span>
                  <span className="sidebar-link-label">{label}</span>
                  {page !== 'position-sizer' ? sidebarProBadge : null}
                </button>
              </li>
            ))}
          </ul>
          {['blowout_risk', 'rule_risk', 'hedging_risk', 'prop_firm_planner'].some((k) => canSeeFeature(k)) ? (
          <>
          <div className="sidebar-section-title sidebar-subsection-title" title="For futures traders working toward or trading a funded prop firm account">Prop firm</div>
          <ul className="sidebar-section-list">
            {canSeeFeature('blowout_risk') && (
            <li>
              <button
                type="button"
                className={`sidebar-link ${blowoutActive ? 'active' : ''}${lockedClass('blowout_risk')}`}
                onClick={() => handleModeChange('risk')}
              >
                <span className="sidebar-link-icon"><IconTrendDown size={18} /></span>
                <span className="sidebar-link-label">Blowout Risk</span>
                {proBadgeUnlessLocked('blowout_risk', sidebarProBadge)}
              </button>
            </li>
            )}
            {canSeeFeature('rule_risk') && (
            <li>
              <button
                type="button"
                className={`sidebar-link ${ruleActive ? 'active' : ''}${lockedClass('rule_risk')}`}
                onClick={() => handleModeChange('apexMae')}
              >
                <span className="sidebar-link-icon"><IconScale size={18} /></span>
                <span className="sidebar-link-label">% Rule Risk</span>
                {proBadgeUnlessLocked('rule_risk', sidebarProBadge)}
              </button>
            </li>
            )}
            {canSeeFeature('hedging_risk') && (
            <li>
              <button
                type="button"
                className={`sidebar-link ${hedgingRiskActive ? 'active' : ''}${lockedClass('hedging_risk')}`}
                onClick={() => handleNavigate('hedging-risk')}
              >
                <span className="sidebar-link-icon"><IconHedge size={18} /></span>
                <span className="sidebar-link-label">Hedging Risk</span>
                {proBadgeUnlessLocked('hedging_risk', sidebarProBadge)}
              </button>
            </li>
            )}
            {canSeeFeature('prop_firm_planner') && (
            <li>
              <button
                type="button"
                className={`sidebar-link ${propPlannerActive ? 'active' : ''}${lockedClass('prop_firm_planner')}`}
                onClick={() => handleNavigate('prop-firm-planner')}
              >
                <span className="sidebar-link-icon"><IconTarget size={18} /></span>
                <span className="sidebar-link-label">Prop Firm Planner</span>
                {proBadgeUnlessLocked('prop_firm_planner', sidebarProBadge)}
              </button>
            </li>
            )}
          </ul>
          </>
          ) : null}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-title">Learn</div>
          <ul className="sidebar-section-list">
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'about' ? 'active' : ''}`}
                onClick={() => handleNavigate('about')}
              >
                <span className="sidebar-link-icon"><IconBook size={18} /></span>
                <span>About</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'updates' ? 'active' : ''}`}
                onClick={() => handleNavigate('updates')}
              >
                <span className="sidebar-link-icon"><IconList size={18} /></span>
                <span>RiskLo Update Log</span>
              </button>
            </li>
            {/* Vector Capital page link removed — the scenario dashboard lives on
                Overview now. The page itself is still reachable at /vector-capital
                for admins. */}
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'how-we-calculate' ? 'active' : ''}`}
                onClick={() => handleNavigate('how-we-calculate')}
              >
                <span className="sidebar-link-icon"><IconCalculator size={18} /></span>
                <span>How We Calculate</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'faq' ? 'active' : ''}`}
                onClick={() => handleNavigate('faq')}
              >
                <span className="sidebar-link-icon"><IconHelp size={18} /></span>
                <span>FAQ</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'tutorials' ? 'active' : ''}`}
                onClick={() => handleNavigate('tutorials')}
              >
                <span className="sidebar-link-icon"><IconBook size={18} /></span>
                <span>Tutorials</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'consistency-rules' || currentPage === 'apex-30-percent-rule' ? 'active' : ''}`}
                onClick={() => handleNavigate('consistency-rules')}
              >
                <span className="sidebar-link-icon"><IconFile size={18} /></span>
                <span>Consistency Rules</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'how-to-export-csv' ? 'active' : ''}`}
                onClick={() => handleNavigate('how-to-export-csv')}
              >
                <span className="sidebar-link-icon"><IconUpload size={18} /></span>
                <span>How to Export CSV</span>
              </button>
            </li>
            {SHOW_DESKTOP_APP_GUIDE_MENU && (
              <li>
                <button
                  type="button"
                  className={`sidebar-link ${currentPage === 'desktopAppGuide' ? 'active' : ''}`}
                  onClick={() => handleNavigate('desktopAppGuide')}
                >
                  <span className="sidebar-link-icon"><IconUpload size={18} /></span>
                  <span>Desktop App Guide</span>
                </button>
              </li>
            )}
          </ul>
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-title">Account</div>
          <ul className="sidebar-section-list">
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'security' ? 'active' : ''}`}
                onClick={() => handleNavigate('security')}
              >
                <span className="sidebar-link-icon"><IconShield size={18} /></span>
                <span>Security &amp; Data</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'terms-and-conditions' ? 'active' : ''}`}
                onClick={() => handleNavigate('terms-and-conditions')}
              >
                <span className="sidebar-link-icon"><IconFile size={18} /></span>
                <span>Terms</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`sidebar-link ${currentPage === 'privacy-policy' ? 'active' : ''}`}
                onClick={() => handleNavigate('privacy-policy')}
              >
                <span className="sidebar-link-icon"><IconFile size={18} /></span>
                <span>Privacy</span>
              </button>
            </li>
          </ul>
        </div>

        {user && (isAdmin || isVectorAdmin) && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Admin</div>
            <ul className="sidebar-section-list">
              {/* Brand Admins: add strategies, connect live data, choose what members see. */}
              <li>
                <button
                  type="button"
                  className={`sidebar-link sidebar-link-admin ${currentPage === 'brand-strategies' ? 'active' : ''}`}
                  onClick={() => handleNavigate('brand-strategies')}
                >
                  <span className="sidebar-link-icon"><IconPlus size={18} /></span>
                  <span>Strategies</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={`sidebar-link sidebar-link-admin ${currentPage === 'brand-settings' ? 'active' : ''}`}
                  onClick={() => handleNavigate('brand-settings')}
                >
                  <span className="sidebar-link-icon"><IconDashboard size={18} /></span>
                  <span>Brand settings</span>
                </button>
              </li>
              {isAdmin && (
                <li>
                  <button
                    type="button"
                    className={`sidebar-link sidebar-link-admin ${currentPage === 'admin-alerts' ? 'active' : ''}`}
                    onClick={() => handleNavigate('admin-alerts')}
                  >
                    <span className="sidebar-link-icon"><IconAlert size={18} /></span>
                    <span className="sidebar-link-label">Alerts</span>
                    {adminAlertCount > 0 ? (
                      <span className="sidebar-pending-count" aria-label={`${adminAlertCount} unacknowledged alerts`}>
                        {adminAlertCount}
                      </span>
                    ) : null}
                  </button>
                </li>
              )}
              {isAdmin && (
                <li>
                  <button
                    type="button"
                    className={`sidebar-link sidebar-link-admin ${currentPage === 'admin-site-metrics' ? 'active' : ''}`}
                    onClick={() => handleNavigate('admin-site-metrics')}
                  >
                    <span className="sidebar-link-icon"><IconChart size={18} /></span>
                    <span>Site metrics</span>
                  </button>
                </li>
              )}
              {/* Strategies are created directly now; this only clears older pending requests. */}
              {isAdmin && pendingSubmissionCount > 0 && (
                <li>
                  <button
                    type="button"
                    className={`sidebar-link sidebar-link-admin ${currentPage === 'admin-strategy-submission-review' ? 'active' : ''}`}
                    onClick={() => handleNavigate('admin-strategy-submission-review')}
                  >
                    <span className="sidebar-link-icon"><IconList size={18} /></span>
                    <span className="sidebar-link-label">Review submissions</span>
                    {pendingSubmissionCount > 0 ? (
                      <span className="sidebar-pending-count" aria-label={`${pendingSubmissionCount} pending`}>
                        {pendingSubmissionCount}
                      </span>
                    ) : null}
                  </button>
                </li>
              )}
              <li>
                <button
                  type="button"
                  className={`sidebar-link sidebar-link-admin ${currentPage === 'admin-logs' ? 'active' : ''}`}
                  onClick={() => handleNavigate('admin-logs')}
                >
                  <span className="sidebar-link-icon"><IconList size={18} /></span>
                  <span>Logs</span>
                </button>
              </li>
              {isAdmin && (
              <li>
                <button
                  type="button"
                  className={`sidebar-link sidebar-link-admin ${currentPage === 'admin-ai' ? 'active' : ''}`}
                  onClick={() => handleNavigate('admin-ai')}
                >
                  <span className="sidebar-link-icon"><IconZap size={18} /></span>
                  <span>RiskLo AI</span>
                </button>
              </li>
              )}
              {showLegacyTools && (
              <li>
                <button
                  type="button"
                  className={`sidebar-link sidebar-link-admin ${currentPage === 'admin-metrics-history' ? 'active' : ''}`}
                  onClick={() => handleNavigate('admin-metrics-history')}
                >
                  <span className="sidebar-link-icon"><IconChart size={18} /></span>
                  <span>Metrics History</span>
                </button>
              </li>
              )}
              <li>
                <button
                  type="button"
                  className={`sidebar-link sidebar-link-admin ${currentPage === 'admin-users' ? 'active' : ''}`}
                  onClick={() => handleNavigate('admin-users')}
                >
                  <span className="sidebar-link-icon"><IconChart size={18} /></span>
                  <span>Users</span>
                </button>
              </li>
              {/* Entities span all brands, so this is FULL admin only — the API
                  enforces it too; hiding the link is convenience, not security. */}
              {isAdmin ? (
                <li>
                  <button
                    type="button"
                    className={`sidebar-link sidebar-link-admin ${currentPage === 'admin-entities' ? 'active' : ''}`}
                    onClick={() => handleNavigate('admin-entities')}
                  >
                    <span className="sidebar-link-icon"><IconDashboard size={18} /></span>
                    <span>Brands</span>
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        {onToggleTheme && (
          <ThemeToggle theme={theme} onToggle={onToggleTheme} collapsed={collapsed} />
        )}
        {tierReady && user && !isPro && onUpgrade && (
          <button
            type="button"
            className="sidebar-upgrade-btn"
            onClick={handleUpgrade}
            title={collapsed ? 'Upgrade to Pro' : undefined}
            aria-label="Upgrade to Pro"
          >
            <span className="sidebar-link-icon"><IconStar size={18} /></span>
            <span className="sidebar-upgrade-btn-label">Upgrade to Pro</span>
          </button>
        )}

        {user ? (
          <button
            type="button"
            className={`sidebar-account-chip ${currentPage === 'account' ? 'active' : ''}`}
            onClick={() => handleNavigate('account')}
            aria-label="Open account settings"
          >
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                aria-hidden="true"
                className="sidebar-user-avatar"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="sidebar-user-avatar sidebar-user-avatar-fallback">
                {initials || 'U'}
              </span>
            )}
            <div className="sidebar-user-meta">
              <span className="sidebar-user-name">{user.name || user.email}</span>
              {user.name && user.email && (
                <span className="sidebar-user-email">{user.email}</span>
              )}
            </div>
            <span className="sidebar-account-chip-chevron" aria-hidden="true">›</span>
          </button>
        ) : (
          <button
            type="button"
            className={`sidebar-signin-chip ${currentPage === 'login' ? 'active' : ''}`}
            onClick={() => handleNavigate('login')}
          >
            <span className="sidebar-user-avatar sidebar-user-avatar-fallback" aria-hidden="true">
              <IconUser size={16} />
            </span>
            <div className="sidebar-user-meta">
              <span className="sidebar-user-name">Sign in</span>
              <span className="sidebar-user-email">Save your work &amp; preferences</span>
            </div>
            <span className="sidebar-account-chip-chevron" aria-hidden="true">›</span>
          </button>
        )}
      </div>
      </aside>
    </>
  );
}

export default Sidebar;
